import { Box, Drawer, Flex, Portal, useBreakpointValue } from "@chakra-ui/react";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { InfoWindow, Map as GoogleMap, useMap } from "@vis.gl/react-google-maps";
import { useEffect, useMemo, useRef, useState } from "react";

import PriceMarker from "@/components/general/Search/PriceMarker";
import { googleMapsID } from "@/config/config";
import { useSearchHover } from "@/pages/Buscar/SearchHoverContext";
import { computeGraceM } from "@/pages/Buscar/useSearchState";
import { normalizePhotoUrl } from "@/utilities/helpers/normalizePhotoUrl";

import BoundaryButton from "./BoundaryButton";
import BoundaryOverlay from "./BoundaryOverlay";
import BrandingChip from "./BrandingChip";
import { hoverIdForDev, hoverIdForProp } from "./buildHoverId";
import DebugBoundaryOverlay from "./DebugBoundaryOverlay";
import DibujarButton from "./DibujarButton";
import DrawBanner from "./DrawBanner";
import DrawingOverlay from "./DrawingOverlay";
import MarkerPreviewCard from "./MarkerPreviewCard";
import RecenterButton from "./RecenterButton";

const DEFAULT_CENTER = { lat: 20.6596988, lng: -103.3496092 }; // Guadalajara
const DEFAULT_ZOOM = 12;

// Debug-only per-polygon numeric panel. Surfaces the actual radius values so
// you can disambiguate "no gray ring drawn" from "gray ring coincides with
// navy" (e.g. Guadalajara: raw == rendered, both clamps pass through).
const fmtM = (m) => (Number.isFinite(m) ? `${Math.round(m).toLocaleString()}m` : "—");
const PolygonDebugInfo = ({ polygon, index }) => {
    if (polygon?.shape !== "circle") {
        const verts = Array.isArray(polygon?.coords) ? polygon.coords.length : 0;
        return (
            <Box mb={1}>
                P{index}: <b>{polygon?.origin ?? "?"}</b> · polygon, {verts} verts
            </Box>
        );
    }
    const rendered = polygon.coords?.radius;
    const raw = polygon.coords?.rawRadius;
    const grace = computeGraceM(polygon);
    const filter = Number.isFinite(grace) ? rendered + grace : rendered;
    let rawNote = "null (URL hydrate)";
    if (Number.isFinite(raw)) {
        const delta = raw - rendered;
        if (Math.abs(delta) < 1) rawNote = `${fmtM(raw)} (= rendered, no clamp)`;
        else if (raw > rendered) rawNote = `${fmtM(raw)} (capped → ${fmtM(rendered)})`;
        else rawNote = `${fmtM(raw)} (floored → ${fmtM(rendered)})`;
    }
    return (
        <Box mb={1}>
            <Box>
                P{index}: <b>{polygon.origin ?? "?"}</b>
            </Box>
            <Box pl={2}>rendered: {fmtM(rendered)}</Box>
            <Box pl={2}>
                grace: {fmtM(grace)} → filter: {fmtM(filter)}
            </Box>
            <Box pl={2}>raw: {rawNote}</Box>
        </Box>
    );
};

// Debug-only legend row. Lives in MapPane (rather than its own file) since
// it's only used by the env-gated debug path and the branch-only nature of
// the overlay isn't worth the file overhead. swatch variants:
//   - "line"   → solid horizontal bar (default)
//   - "dashed" → short stroked dashes (matches dashed map rings)
//   - "square" → filled translucent square (matches filled map regions)
const LegendRow = ({ color, label, swatch = "line" }) => {
    let swatchEl;
    if (swatch === "square") {
        swatchEl = (
            <Box
                w="12px"
                h="12px"
                bg={color}
                opacity={0.55}
                border="1px solid"
                borderColor={color}
                flexShrink={0}
            />
        );
    } else if (swatch === "dashed") {
        swatchEl = <Box w="14px" h="0" borderTop="2px dashed" borderColor={color} flexShrink={0} />;
    } else {
        swatchEl = <Box w="14px" h="3px" bg={color} flexShrink={0} />;
    }
    return (
        <Flex align="center" gap={2} my={0.5} lineHeight="1.2">
            {swatchEl}
            <Box>{label}</Box>
        </Flex>
    );
};

// Great-circle distance in meters. Used to derive the API search radius from
// the visible map viewport — diagonal/2 (center → NE corner) covers roughly
// the whole rectangle while staying close to the user's actual focus at any
// zoom level. Avoids loading google.maps.geometry; a few lines is cheaper.
const haversineDistanceM = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
};

const parsePosition = (item) => {
    const lat = parseFloat(item?.location_props?.latitude);
    const lng = parseFloat(item?.location_props?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
};

// Property price: first non-empty operation field matching what the user is
// searching for. Matches the pricing convention used by ResultsPane's
// pickPriceField — keeping the marker price aligned with the card price.
const pickPropertyMarkerPrice = (item, operation) => {
    const wants = (k) => !operation || operation.length === 0 || operation.includes(k);
    if (wants("LEASE") && item.lease) return item.lease;
    if (wants("SALE") && item.sale) return item.sale;
    if (wants("PRESALE") && item.presale) return item.presale;
    return item.sale || item.lease || item.presale || null;
};

const pickDevelopmentMarkerPrice = (dev) =>
    dev.min_presale || dev.min_price || dev.min_sale || dev.min_lease;

// Cluster bubble icon. Drawn as a white outer ring + colored inner disc —
// the white border is the Zillow trick that keeps adjacent bubbles
// visually distinct when they overlap. `highlighted` is a pure
// proportional scale-up (1.25× on radius AND border) — no halo, no glow.
// Border thickness is 8% of the base radius so the ring stays
// proportional across the count range and the highlight reads as "same
// bubble, bigger" rather than "different bubble".
const buildClusterIcon = ({ count, isDevMajority, highlighted }) => {
    const fillColor = isDevMajority ? "#FFCD00" : "#002B49";
    const baseRadius = Math.min(28, 14 + Math.log2(count) * 3);
    const baseBorder = baseRadius * 0.08;
    const scale = highlighted ? 1.25 : 1;
    const outerR = baseRadius * scale;
    const innerR = (baseRadius - baseBorder) * scale;
    // Tiny pad so the soft drop shadow (rendered by `boxShadow`-like CSS on
    // the host element via Google Maps' marker shadow) doesn't get clipped.
    const pad = 1;
    const size = (outerR + pad) * 2;
    const c = size / 2;
    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">
            <circle cx="${c}" cy="${c}" r="${outerR}" fill="white"/>
            <circle cx="${c}" cy="${c}" r="${innerR}" fill="${fillColor}"/>
        </svg>`.trim();
    return {
        url: `data:image/svg+xml;base64,${window.btoa(svg)}`,
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(c, c),
    };
};

// Cluster label fontSize must scale with the bubble — otherwise the number
// looks lost inside a hovered cluster. Same 1.25× factor as the icon.
const buildClusterLabel = ({ count, isDevMajority, highlighted }) => {
    const base = count >= 100 ? 11 : 12;
    const size = highlighted ? base * 1.25 : base;
    return {
        text: String(count),
        color: isDevMajority ? "#002B49" : "white",
        fontSize: `${size}px`,
        fontWeight: "700",
    };
};

// Cluster bubble. Counts how many of the clustered markers were tagged
// `_guiaKind === "development"` — if that's ≥50% of the cluster, render the
// bubble in Guia yellow (with navy text); otherwise navy with white text.
// Mirrors the individual-marker color split so the map reads consistently
// at any zoom level. Markers are tagged in MarkersLayer's ref callback.
//
// onMouseOver/Out fire when the user's cursor enters/leaves the bubble,
// letting MarkersLayer apply the same highlight visual whether the trigger
// was direct mouse hover OR a card hover from the results pane.
const buildClusterRenderer = ({ onMouseOver, onMouseOut }) => ({
    render({ count, position, markers }) {
        const devCount = (markers || []).reduce(
            (n, m) => n + (m && m._guiaKind === "development" ? 1 : 0),
            0
        );
        const isDevMajority = count > 0 && devCount / count >= 0.5;
        const marker = new window.google.maps.Marker({
            position,
            icon: buildClusterIcon({ count, isDevMajority, highlighted: false }),
            label: buildClusterLabel({ count, isDevMajority, highlighted: false }),
            zIndex: 1000 + count,
        });
        // Stash so the highlight effect can rebuild the icon (count + dev
        // majority determine color and base size) without re-walking the
        // member markers.
        marker._guiaClusterMeta = { count, isDevMajority };
        if (onMouseOver) marker.addListener("mouseover", () => onMouseOver(marker));
        if (onMouseOut) marker.addListener("mouseout", () => onMouseOut(marker));
        return marker;
    },
});

// Captures the google.maps.Map instance into a ref the parent (BuscarLayout)
// owns. useMap() only resolves inside a <Map> context, so this tiny child
// is the bridge — the parent can then drive imperative map APIs (fitBounds
// for the auto-polygon path, the P6.4 Re-centrar action) without pushing
// every camera-shaping call back down into MapPane.
const CaptureMapRef = ({ mapRef }) => {
    const map = useMap();
    useEffect(() => {
        if (!mapRef) return;
        mapRef.current = map ?? null;
        if (typeof window !== "undefined" && process.env.NEXT_PUBLIC_BUSCAR_DEBUG === "1") {
            window.__buscarMap = map ?? null;
        }
        return () => {
            if (mapRef.current === map) mapRef.current = null;
        };
    }, [map, mapRef]);
    return null;
};

// useMap() only resolves inside a <Map> context, so the clusterer
// orchestration lives in a child component the parent mounts under <Map>.
// MarkerClusterer wraps the underlying AdvancedMarkerElement instances —
// vis.gl renders each <AdvancedMarker> as React; we capture the native DOM
// element via ref and hand the bag to MarkerClusterer, which decides on
// each idle whether to show individuals or aggregate cluster bubbles.
const MarkersLayer = ({ markers, selectedId, onSelect }) => {
    const { hoveredId, setHoveredId } = useSearchHover();
    const map = useMap();
    const clustererRef = useRef(null);
    // Native AdvancedMarkerElement instances, keyed by marker id. Kept in a
    // ref (not state) on purpose — every ref callback would otherwise trigger
    // a re-render, and inline arrow-function refs would also fire on every
    // re-render with (null, element). At 800+ markers that's an infinite
    // thrash. Refs let us collect instances silently; the markers prop drives
    // the actual clusterer sync via useEffect.
    const markerInstancesRef = useRef(new Map());
    // What's currently in the clusterer. Used to diff against
    // markerInstancesRef so the sync only adds/removes the delta instead of
    // clearing all and re-adding (which triggers a full cluster recompute
    // and visible churn). Without this, four cluster passes fire on initial
    // load — one each time refs settle in a new batch — and each pass
    // briefly removes all markers before re-rendering clusters → flash.
    const addedMarkersRef = useRef(new Map());

    // Live id → kind lookup. Used inside the (memoized) ref callbacks to tag
    // each captured AdvancedMarkerElement with `_guiaKind` so the cluster
    // renderer can count devs vs properties and pick navy/yellow per cluster.
    // Held in a ref so the stable ref callbacks (memoized once on mount)
    // always see the latest mapping without re-creating themselves.
    const variantByIdRef = useRef(new Map());
    // Live id → hoverIds[] lookup. Tagged onto each captured marker as
    // `_guiaHoverIds` so the cluster-bubble highlight effect can quickly
    // find which cluster contains the listing the user is hovering. Stack
    // markers contribute ALL of their member hoverIds, so hovering any
    // bucketed card lights up the same cluster.
    const hoverIdsByIdRef = useRef(new Map());
    for (const m of markers) {
        variantByIdRef.current.set(m.id, m.variant);
        hoverIdsByIdRef.current.set(m.id, m.hoverIds);
    }

    // vis.gl mounts AdvancedMarker elements over multiple frames — refs fire
    // staggered. We need ONE clusterer sync once they all settle, not one per
    // frame (which would clearMarkers + addMarkers in a thrash that never
    // gives the algorithm a stable frame to compute clusters). Trailing-edge
    // debounce: each new ref pushes the sync deadline out 120ms; the actual
    // clusterer update runs once after a quiet period.
    const syncTimeoutRef = useRef(null);
    const scheduleSync = () => {
        if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = setTimeout(() => {
            syncTimeoutRef.current = null;
            const clusterer = clustererRef.current;
            if (!clusterer) return;

            // Diff: what's in markerInstancesRef now vs. what we've already
            // handed to the clusterer. Only push the delta. clearMarkers +
            // full addMarkers from scratch would trigger a full cluster
            // recompute on every sync and the user sees marker churn.
            const current = markerInstancesRef.current;
            const added = addedMarkersRef.current;

            const toAdd = [];
            for (const [id, marker] of current) {
                if (!added.has(id)) toAdd.push(marker);
            }
            const toRemove = [];
            for (const [id, marker] of added) {
                if (!current.has(id)) toRemove.push(marker);
            }

            if (toAdd.length > 0) clusterer.addMarkers(toAdd);
            if (toRemove.length > 0) clusterer.removeMarkers(toRemove);

            // Snapshot current as the new "already added" set.
            addedMarkersRef.current = new Map(current);

            // firstSyncDone is set by the `clusteringend` listener (above) —
            // that fires after the clusterer actually hides the grouped
            // individuals, closing the brief reveal window.
        }, 120);
    };

    // Stable per-id ref callback factory. Memoized once on mount so each
    // PriceMarker gets the SAME callback across re-renders → React doesn't
    // fire the ref unless the underlying AdvancedMarkerElement actually
    // changes.
    const refCallbacksRef = useRef(new Map());
    const getMarkerRef = (id) => {
        let cb = refCallbacksRef.current.get(id);
        if (!cb) {
            cb = (marker) => {
                if (marker) {
                    // Tag with kind so the cluster renderer can count
                    // dev-majority vs property-majority clusters.
                    marker._guiaKind = variantByIdRef.current.get(id);
                    // Tag with the full hoverIds set so the cluster-bubble
                    // highlight effect catches all member listings.
                    marker._guiaHoverIds = hoverIdsByIdRef.current.get(id);
                    markerInstancesRef.current.set(id, marker);
                } else {
                    markerInstancesRef.current.delete(id);
                }
                scheduleSync();
            };
            refCallbacksRef.current.set(id, cb);
        }
        return cb;
    };

    // "Cluster the cursor is currently over" — set by the cluster's mouseover
    // listener, cleared on mouseout. Held in a ref (avoid re-renders on every
    // mouse move). The tick bumps on each change so the highlight effect
    // below re-runs and applies/reverts the visual.
    const directHoverClusterRef = useRef(null);
    const [directHoverTick, setDirectHoverTick] = useState(0);
    // Bumps on every cluster recomputation so the highlight effect can
    // re-target the new cluster set after pan/zoom (old cluster markers go
    // away when the clusterer regenerates).
    const [clusterEpoch, setClusterEpoch] = useState(0);

    // Spin up the clusterer the first time the map is ready. Reusing the same
    // instance across renders avoids the expensive re-attach + cluster recalc
    // that would happen on a fresh `new MarkerClusterer({ map })` per render.
    useEffect(() => {
        if (!map) return;
        if (!clustererRef.current) {
            const c = new MarkerClusterer({
                map,
                renderer: buildClusterRenderer({
                    onMouseOver: (clusterMarker) => {
                        directHoverClusterRef.current = clusterMarker;
                        setDirectHoverTick((t) => t + 1);
                    },
                    onMouseOut: (clusterMarker) => {
                        if (directHoverClusterRef.current === clusterMarker) {
                            directHoverClusterRef.current = null;
                            setDirectHoverTick((t) => t + 1);
                        }
                    },
                }),
            });
            // `clusteringend` fires AFTER the clusterer's render pass — after
            // it has called marker.map = null on the ones it grouped and
            // mounted cluster bubbles. Flipping the reveal gate here (rather
            // than synchronously after addMarkers) closes the ~16ms window
            // where all individual markers were briefly visible before the
            // cluster pass hid them. The listener is idempotent — subsequent
            // clusteringend events re-fire setFirstSyncDone(true) which is a
            // no-op. clusterEpoch bumps too so the highlight effect re-targets
            // the new cluster set after pan/zoom.
            c.addListener("clusteringend", () => {
                setFirstSyncDone(true);
                setClusterEpoch((n) => n + 1);
            });
            clustererRef.current = c;
        }
    }, [map]);

    // Hide individual price pills until the first sync settles — without
    // this, all 800+ AdvancedMarkers paint individually for ~120ms before
    // MarkerClusterer hides the ones it groups, producing a maximalist
    // flash. Cluster bubbles only exist post-sync, so they never flash.
    const [firstSyncDone, setFirstSyncDone] = useState(false);

    // When the markers prop changes (e.g., a filter narrows results), the
    // ref-callbacks already trigger scheduleSync for ANY mounted/unmounted
    // PriceMarkers — no need for a separate effect here. Removed to avoid
    // duplicate sync calls on the same prop update.

    // Tear down on unmount — drops the marker overlay and any pending
    // animation/transition handlers from the clusterer. Reset the tracking
    // Map too so a future remount starts clean.
    useEffect(() => {
        return () => {
            clustererRef.current?.clearMarkers();
            clustererRef.current = null;
            addedMarkersRef.current = new Map();
        };
    }, []);

    // Cluster bubble highlight. Same visual as a marker hover (scale + halo +
    // glow), triggered from either:
    //   - hovering a card whose listing's marker is currently inside a cluster
    //   - hovering the cluster bubble directly with the mouse
    // No standalone overlay marker — the cluster IS the highlight target,
    // which avoids the overlay/real-marker pointer-event tug-of-war that
    // caused the hover bounce on directly-hovered individual markers.
    const highlightedClusterRef = useRef(null);
    useEffect(() => {
        // Revert previously-highlighted cluster (icon + label + z).
        if (highlightedClusterRef.current) {
            const { marker, normalIcon, normalLabel, normalZ } = highlightedClusterRef.current;
            if (marker) {
                marker.setIcon(normalIcon);
                marker.setLabel(normalLabel);
                if (normalZ != null) marker.setZIndex(normalZ);
            }
            highlightedClusterRef.current = null;
        }
        // Pick a target. Direct mouse-over takes precedence (matches the
        // marker case where the directly-hovered element wins).
        let target = directHoverClusterRef.current;
        if (!target && hoveredId) {
            const clusters = clustererRef.current?.clusters || [];
            for (const cluster of clusters) {
                const hit = (cluster.markers || []).some(
                    (m) => m && (m._guiaHoverIds || []).includes(hoveredId)
                );
                if (hit) {
                    target = cluster.marker;
                    break;
                }
            }
        }
        if (!target?._guiaClusterMeta) return;
        const meta = target._guiaClusterMeta;
        const normalIcon = target.getIcon();
        const normalLabel = target.getLabel();
        const normalZ = target.getZIndex();
        target.setIcon(buildClusterIcon({ ...meta, highlighted: true }));
        target.setLabel(buildClusterLabel({ ...meta, highlighted: true }));
        target.setZIndex(99999);
        highlightedClusterRef.current = {
            marker: target,
            normalIcon,
            normalLabel,
            normalZ,
        };
    }, [hoveredId, directHoverTick, clusterEpoch]);

    return markers.map((m) => {
        // Stack markers light up when any of their bucketed listings is the
        // hovered one — so card→marker feedback works for all N listings
        // sharing one coord.
        const isHovered = hoveredId != null && (m.hoverIds || []).includes(hoveredId);
        // Hover signal on marker → card. Singles point at their one entry.
        // Stacks DON'T pick an arbitrary entry: "first in the bucket" is
        // arbitrary (bucket order has no UX meaning), and highlighting one
        // ResultsPane card while the user is opening a stack popup that
        // shows ALL entries reads as "this specific one is special" — not
        // true. Card → stack-marker still works the other way via
        // `m.hoverIds.includes(hoveredId)`, so the bidirectional sync is
        // preserved for the meaningful direction (specific listing → its
        // location).
        const onPointerEnterHoverId = m.stackCount === 1 ? m.hoverIds[0] : null;
        return (
            <PriceMarker
                key={m.id}
                markerRef={getMarkerRef(m.id)}
                position={m.position}
                price={m.price}
                variant={m.variant}
                stackCount={m.stackCount}
                isSelected={selectedId === m.id}
                isHovered={isHovered}
                // Lift the hovered marker above its neighbors so it
                // isn't occluded by adjacent overlapping markers.
                zIndex={isHovered ? 9999 : undefined}
                onClick={() => onSelect(m.id)}
                onPointerEnter={() => {
                    if (onPointerEnterHoverId) setHoveredId(onPointerEnterHoverId);
                    // Hover-preload the marker's photo(s) so a subsequent
                    // click → MarkerPreviewCard already has them in the
                    // browser cache. Cheap because (a) `new Image()` is
                    // small, and (b) repeat hovers hit the HTTP cache and
                    // don't re-fetch. Skip for clusters (would prefetch
                    // unrelated bucket members) — only fire for the
                    // single-marker and stack-of-N-at-same-coord cases,
                    // both of which are what the popup will show.
                    if (typeof window !== "undefined" && window.Image) {
                        for (const e of m.entries || []) {
                            // Normalize before preloading, otherwise we warm
                            // the cache with the 404 URL and the popup still
                            // pays for a fresh fetch of the real one.
                            const url = normalizePhotoUrl(e?.data?.photo);
                            if (url) {
                                const img = new window.Image();
                                img.src = url;
                            }
                        }
                    }
                }}
                onPointerLeave={() => setHoveredId(null)}
                hidden={!firstSyncDone}
            />
        );
    });
};

// Hide Google's InfoWindow tail/arrow. The default tail anchors at the
// popup's bottom pointing to the marker — fine when the popup is above the
// marker (default), but wrong when SelectedMarkerInfoWindow flips it below
// (the tail would then point AWAY from the marker). Cleaner to just drop
// the tail and let the popup read as a free-floating card in either
// position. Scoped to the InfoWindow's internal class, so it doesn't bleed
// into the rest of the app.
// Rendered via dangerouslySetInnerHTML, not as a text child: React escapes
// text children (the apostrophes/quotes in these CSS comments become HTML
// entities in the server output) while the client renders the raw string,
// which trips a hydration text-mismatch error now that this component
// actually renders during SSR.
const HIDE_INFO_WINDOW_TAIL_CSS = `
    .gm-style-iw-tc { display: none !important; }
    /* Google's default padding on .gm-style-iw-c is asymmetric
       (12px 0 0 12px) — 12px on top + left, zero on right + bottom —
       reserved for the auto-placed close button at top-right. We
       disable that button via headerDisabled, so the asymmetry just
       reads as "card clipped at the right and bottom edges." Force
       symmetric padding so the card has matching breathing room on
       all four sides. */
    .gm-style-iw-c {
        padding: 12px !important;
        /* Cap the popup chrome at viewport width minus a small breathing
           margin. Without this Google sometimes lets the popup extend
           past the visible map area when the marker is near an edge —
           the popup wrapper "clamps" but the inner card keeps its 300px
           width, so title + address text get clipped off the right
           (visible on phones < ~400px viewport). Capping the chrome
           itself keeps both Google's positioning math and the card
           aligned inside the visible area. */
        max-width: calc(100vw - 24px) !important;
    }
    /* Strip Google's auto-applied max-height + scroll so SelectedMarker-
       InfoWindow's own Box maxH is the single scroll surface. Without
       this Google caps the inner container around 440px and creates a
       second scrollbar competing with ours. */
    .gm-style-iw-d {
        max-height: none !important;
        overflow: visible !important;
    }
`;

const HideInfoWindowTail = () => (
    <style dangerouslySetInnerHTML={{ __html: HIDE_INFO_WINDOW_TAIL_CSS }} />
);

// Shared body for both desktop InfoWindow and mobile Drawer. Single-listing
// markers render one MarkerPreviewCard; stack markers render a vertical
// scroll list. `maxH` only constrains the stack list — singles size
// themselves. Border-on-all-but-last separates white cards inside a white
// container. Cards are centered within their row (matters on mobile where
// Drawer.Body is wider than MarkerPreviewCard's capped 300px width).
const SelectedMarkerCards = ({ selected, state, maxH }) => {
    const isStack = (selected.entries?.length ?? 1) > 1;
    if (!isStack) {
        return (
            <Flex justify="center">
                <MarkerPreviewCard entry={selected.entries[0]} state={state} />
            </Flex>
        );
    }
    return (
        <Box maxH={maxH ? `${maxH}px` : undefined} overflowY="auto" pr={1}>
            {selected.entries.map((entry, i) => {
                const isLast = i === selected.entries.length - 1;
                return (
                    <Flex
                        key={i}
                        justify="center"
                        pb={isLast ? 0 : 3}
                        mb={isLast ? 0 : 3}
                        borderBottomWidth={isLast ? 0 : "1px"}
                        borderColor="gray.200"
                    >
                        <MarkerPreviewCard entry={entry} state={state} />
                    </Flex>
                );
            })}
        </Box>
    );
};

// Mobile marker preview — Chakra Drawer placement="bottom". Slides up from
// the bottom of the viewport. The map stays visible behind a translucent
// backdrop so the user keeps their spatial context. Close paths: backdrop
// tap, Esc (Drawer handles internally), or clicking a different marker
// (selectedId changes → re-renders with new entry → Drawer stays open with
// new content).
//
// Esc is also caught by MapPane's keydown listener which sets selectedId
// to null → Drawer's `open` flips false → onOpenChange fires with
// {open:false} → we no-op (parent already cleared). No double-close risk.
const SelectedMarkerSheet = ({ selected, state, onClose }) => (
    <Drawer.Root
        open
        onOpenChange={(e) => {
            if (!e.open) onClose();
        }}
        placement="bottom"
    >
        <Portal>
            <Drawer.Backdrop />
            <Drawer.Positioner>
                {/* maxH leaves ~30vh of map visible above the sheet so the
                    user has a tappable backdrop area to close. Without it
                    the sheet covered the full viewport and the drag-handle
                    pill wasn't actually wired to close — only the backdrop
                    click is. Drawer.Body is the scroll container; pass
                    no maxH to SelectedMarkerCards so its outer Box isn't a
                    competing scroll surface. */}
                <Drawer.Content colorPalette="accent" roundedTop="2xl" maxH="70vh">
                    {/* Drag-handle affordance. No Drawer.Title text — the
                        card body is the title. */}
                    <Drawer.Header px={4} py={2} flex="0 0 auto">
                        <Box w="40px" h="4px" rounded="full" bg="gray.300" mx="auto" />
                    </Drawer.Header>
                    <Drawer.Body p={4} pt={2} overflowY="auto">
                        <SelectedMarkerCards selected={selected} state={state} />
                    </Drawer.Body>
                </Drawer.Content>
            </Drawer.Positioner>
        </Portal>
    </Drawer.Root>
);

// Marker-click preview wrapper. Resolves the selected marker once, then
// picks the surface based on viewport + entry count:
//   mobile + stack (>1 entry) → bottom-sheet Drawer (scrollable list)
//   mobile + single OR desktop → anchored InfoWindow (dynamic placement)
//
// Rationale: a single listing fits fine inside the compact popover on any
// screen, and keeping the InfoWindow path for it means the shape is
// consistent between desktop and mobile for the 95% case. Stacks need
// vertical scroll over multiple cards — a bottom sheet matches that need
// better on phones (taller scroll surface, doesn't fight the marker
// anchor math when the stack overflows the viewport).
//
// useBreakpointValue with ssr:false returns undefined on the server.
// Treating undefined as desktop keeps the SSR markup matching the lg+
// render (no hydration mismatch). When the user clicks a marker, the
// breakpoint has long since resolved client-side, so the branch is
// correct in practice.
const SelectedMarkerPopup = ({ markers, selectedId, onClose, state }) => {
    const isMobile = useBreakpointValue(
        { base: true, lg: false },
        { ssr: typeof window === "undefined" }
    );
    // Lookup by marker `id` — stable both per single listing (derived from
    // wiggot_code / dev id) and per stack (derived from rounded coord).
    // Both survive refetches: the refetched markers array contains the same
    // ids for the same listings or coords.
    const selected = useMemo(() => markers.find((m) => m.id === selectedId), [markers, selectedId]);
    if (!selected) return null;
    const isStack = (selected.entries?.length ?? 1) > 1;
    if (isMobile === true && isStack) {
        return <SelectedMarkerSheet selected={selected} state={state} onClose={onClose} />;
    }
    return <SelectedMarkerInfoWindow selected={selected} state={state} onClose={onClose} />;
};

// Desktop marker-click preview. Anchored InfoWindow with smart pixelOffset.
// Body is a compact MarkerPreviewCard (~300×280) — slim enough that Google
// Maps doesn't need to auto-pan to fit it on screen.
//
// `headerDisabled` strips Google's default top bar (~36px of chrome with a
// close button we don't use). No X close button in the body either — Zillow
// doesn't have one. Close paths are: click the map background (wired in
// MapPane onClick), click a different marker (selectedId toggles), Esc
// (listener attached in MapPane while open).
const SelectedMarkerInfoWindow = ({ selected, onClose, state }) => {
    const map = useMap();
    const isStack = (selected.entries?.length ?? 1) > 1;

    // Dynamic placement. Default Google InfoWindow centers horizontally on
    // the marker and paints above it. Both axes can clip off-screen when
    // the marker is near an edge. Compute pixelOffset to shift the popup
    // into the viewport without moving the map. The tail/arrow is hidden
    // globally (would point the wrong way when flipped), so the popup
    // reads as a floating card whatever the offset.
    const mapDiv = map?.getDiv();
    const bounds = map?.getBounds();
    // Cap stack popup at the viewport height minus a small margin — at
    // tight viewports a tall stack would otherwise be physically larger
    // than the map and clip no matter how we offset it (verified: at a
    // 597px viewport, a +1 popup measured 607px tall — bigger than the
    // viewport, no flip-below could rescue it). The same cap drives both
    // approxHeight (for the flip-below threshold) and the maxH on the
    // stack's list Box (so the actual rendered popup matches the math).
    const mapH = mapDiv?.clientHeight ?? 600;
    const popupMaxH = Math.max(240, mapH - 80);
    // Measured rendered heights of MarkerPreviewCard inside the InfoWindow
    // (12px popup padding on each side already included):
    //   - single card → ~280px
    //   - per stacked card → ~280px + 12px bottom-border gap
    // Estimating accurately matters: pxOffsetY pushes the popup bottom down
    // by `approxHeight + edgeMargin`, so the popup TOP ends up at
    // `marker + edgeMargin` ONLY when approxHeight matches the real
    // rendered height. Overestimating leaves a visible gap between the
    // marker and the card (was 340 for singles, leaving a 60px gap).
    const SINGLE_CARD_H = 280;
    const STACK_CARD_GAP = 12;
    const rawStackHeight =
        SINGLE_CARD_H * (selected.entries?.length ?? 1) +
        STACK_CARD_GAP * Math.max(0, (selected.entries?.length ?? 1) - 1) +
        24; // 12px popup padding top + bottom
    const approxHeight = isStack ? Math.min(popupMaxH, rawStackHeight) : SINGLE_CARD_H;
    const approxWidth = 320;
    const edgeMargin = 16;
    let pxOffsetX = 0;
    let pxOffsetY = -8;
    if (bounds && mapDiv) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const xFrac = (selected.position.lng - sw.lng()) / (ne.lng() - sw.lng());
        const yFrac = (ne.lat() - selected.position.lat) / (ne.lat() - sw.lat());
        const markerPxX = xFrac * mapDiv.clientWidth;
        const markerPxY = yFrac * mapDiv.clientHeight;

        // Vertical: flip below if not enough room above. +16 keeps the
        // popup clear of the marker pill rather than overlapping it.
        if (markerPxY < approxHeight + edgeMargin) {
            pxOffsetY = approxHeight + edgeMargin;
        }

        // Horizontal: shift the popup right/left if it would clip past the
        // viewport edge from its centered-on-marker default. Positive
        // pixelOffset.x shifts the popup right.
        const leftEdge = markerPxX - approxWidth / 2;
        const rightEdge = markerPxX + approxWidth / 2;
        if (leftEdge < edgeMargin) {
            pxOffsetX = edgeMargin - leftEdge;
        } else if (rightEdge > mapDiv.clientWidth - edgeMargin) {
            pxOffsetX = mapDiv.clientWidth - edgeMargin - rightEdge;
        }
    }
    const pixelOffset = [pxOffsetX, pxOffsetY];

    return (
        <InfoWindow
            position={selected.position}
            onCloseClick={onClose}
            headerDisabled
            // Stop Google from re-firing its "fit popup in viewport" auto-pan
            // on every idle while the popup is open (it whips the camera back
            // toward the marker the moment you drag). We position the popup
            // ourselves via pixelOffset instead.
            disableAutoPan
            pixelOffset={pixelOffset}
        >
            {/* Stack cards capped at the viewport-derived popupMaxH so the
                popup never grows taller than the map can show. Google's
                `.gm-style-iw-d` is overridden to overflow:visible (see
                HideInfoWindowTail) so SelectedMarkerCards' Box is the
                single scroll surface — no competing scrollbars. */}
            <SelectedMarkerCards selected={selected} state={state} maxH={popupMaxH} />
        </InfoWindow>
    );
};

// Tries to derive a sensible initial center from the first geo-tagged result
// so the map opens framing the user's current search instead of defaulting
// straight to Guadalajara every time. Falls back if no result has coords.
const deriveInitialCenter = (properties, developments) => {
    const all = [...(developments || []), ...(properties || [])];
    for (const item of all) {
        const lat = parseFloat(item?.location_props?.latitude);
        const lng = parseFloat(item?.location_props?.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    }
    return DEFAULT_CENTER;
};

const MapPane = ({
    isCollapsed,
    hidden,
    state,
    properties,
    developments,
    isFetching,
    onBoundsChange,
    onMapDragStart,
    onMapIdle,
    polygons,
    // The user's own boundary entries (excludes a dedicated page's default
    // 'floor' boundary). Overlays/debug paint `polygons`; the control slot
    // below gates on `userPolygons` — floor-only pages show the plain
    // "Dibujar perímetro" and no Borrar (clearing a floor is a no-op).
    userPolygons = [],
    mapRef,
    onClearBoundary,
    onRecenterBoundary,
    cameraDragged,
    isDrawing,
    isDrawPressed,
    drawVertices,
    onStartDraw,
    onCancelDraw,
    onApplyDraw,
    debugEnabled = false,
    debugVisible = false,
    onToggleDebug,
    debugPaddingPx = 80,
}) => {
    // When the map flips from hidden (display:none) back to visible, vis.gl
    // AdvancedMarker overlays don't re-project — the previously-rendered DOM
    // stays at its last (stale, often centroid-stacked) pixel coords. Cluster
    // bubbles (MarkerClusterer's native overlays) re-project fine after a
    // `resize` event, but vis.gl's React-managed AdvancedMarkers need a hard
    // remount to recompute their lat/lng → pixel projection. We bump
    // markersEpoch on every hidden true → false transition and key
    // <MarkersLayer> by it, forcing React to unmount + remount; the new
    // markers get fresh AdvancedMarkerElements that project correctly.
    const [markersEpoch, setMarkersEpoch] = useState(0);
    const prevHiddenRef = useRef(hidden);
    useEffect(() => {
        if (prevHiddenRef.current === true && hidden === false) {
            setMarkersEpoch((e) => e + 1);
        }
        prevHiddenRef.current = hidden;
    }, [hidden]);
    // Initial camera is computed ONCE per mount — re-deriving on every result
    // update would yank the camera around as the user pans / filters. URL-
    // provided lat/lng (e.g. from a shared link) win over the derived center
    // so shared snapshots actually land where the sender was looking;
    // deriveInitialCenter is the fallback for fresh searches with no camera
    // hint. The camera then sync-broadcasts back to URL via onIdle (P3.7), so
    // the round-trip is stable.
    const initialCenter = useMemo(
        () => {
            if (state.latitude != null && state.longitude != null) {
                return { lat: state.latitude, lng: state.longitude };
            }
            return deriveInitialCenter(properties, developments);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const initialZoom = state.zoom ?? DEFAULT_ZOOM;

    // Defer marker render (and other children) until the first idle event so
    // we don't try to overlay before the tile layer is laid out — prevents
    // a perceptible flicker. P3.3+ markers hang off this gate.
    const [mapLoaded, setMapLoaded] = useState(false);

    // Sticky "initial load complete" flag. RTK Query's `isFetching` is true
    // until BOTH properties + developments queries resolve, and they arrive
    // in separate batches → if we render markers as each batch lands, the
    // user sees devs flash in, then properties flash in 1-2s later. We hold
    // marker render until both queries have first resolved; after that flip,
    // we don't re-hide on subsequent filter-refetches (diff sync handles
    // those smoothly).
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    useEffect(() => {
        if (!isFetching && !initialLoadDone) setInitialLoadDone(true);
    }, [isFetching, initialLoadDone]);
    // Bounds live on the parent — see BuscarLayout. ResultsPane filters its
    // grid by them (P3.6) and P3.8's SearchThisAreaButton will read them to
    // detect drift from the result set's center. We just forward each onIdle.
    // Marker-click target — P3.7 wires the InfoWindow body off this. Lives
    // here (not in a shared context) because the list pane doesn't react;
    // hoveredId from SearchHoverContext already handles cross-highlighting.
    const [selectedId, setSelectedId] = useState(null);

    // Debug legend collapse state. Persists for the session so it doesn't
    // keep popping back open after the user has dismissed it.
    const [debugLegendExpanded, setDebugLegendExpanded] = useState(true);

    // Esc dismisses the InfoWindow. Listener is only attached while an
    // InfoWindow is open, so we're not eating Esc presses globally.
    useEffect(() => {
        if (selectedId == null) return;
        const onKey = (e) => {
            if (e.key === "Escape") setSelectedId(null);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [selectedId]);

    // Build the marker render list — combine devs + props, drop items with
    // no usable lat/lng (still searchable, just not mappable). Memoized so
    // PriceMarker children stay referentially stable across pans/zooms that
    // don't actually change the result set.
    //
    // Stable React keys per listing — NO array-index suffix. With an index in
    // the key, the same listing landing at a different position in a refetched
    // array got a new key, unmounting/remounting its PriceMarker (which tore
    // down DOM mid-click + restarted the hover transition + churned the
    // clusterer). Stable keys → React reorders without remounting.
    //
    // Stacking: entries at identical lat/lng (rounded to 5 decimals = ~1m
    // precision) get bucketed into one "stack" marker. Solves the "same
    // building, multiple units" case where 3 listings at one address would
    // otherwise render as 3 overlapping pills (only the top one visible). The
    // stack registers as a SINGLE marker for MarkerClusterer, so it still
    // clusters with geographic neighbors at low zoom; at high zoom it stays
    // grouped instead of breaking apart into invisible duplicates.
    const markers = useMemo(() => {
        const bucketKey = (p) => `${p.lat.toFixed(5)}_${p.lng.toFixed(5)}`;
        // First-write-wins dedupe by id (API returns dups), then bucket by
        // rounded coord.
        const seenIds = new Set();
        const buckets = new Map();

        const pushEntry = ({ id, hoverId, position, price, variant, entry }) => {
            if (seenIds.has(id)) return;
            seenIds.add(id);
            const key = bucketKey(position);
            let bucket = buckets.get(key);
            if (!bucket) {
                bucket = {
                    key,
                    position,
                    entries: [],
                    hoverIds: [],
                    prices: [],
                    variants: [],
                    ids: [],
                };
                buckets.set(key, bucket);
            }
            bucket.entries.push(entry);
            bucket.hoverIds.push(hoverId);
            bucket.prices.push(price);
            bucket.variants.push(variant);
            bucket.ids.push(id);
        };

        (developments || []).forEach((dev) => {
            const position = parsePosition(dev);
            if (!position) return;
            pushEntry({
                id: `dev-${dev.id || dev.wiggot_code || "x"}`,
                hoverId: hoverIdForDev(dev),
                position,
                price: pickDevelopmentMarkerPrice(dev),
                variant: "development",
                entry: { kind: "development", data: dev },
            });
        });
        (properties || []).forEach((item) => {
            const position = parsePosition(item);
            if (!position) return;
            pushEntry({
                id: `prop-${item.wiggot_code || item.id || item.property_id || "x"}`,
                hoverId: hoverIdForProp(item),
                position,
                price: pickPropertyMarkerPrice(item, state.operation),
                variant: "property",
                entry: { kind: "property", data: item },
            });
        });

        // Project each bucket into a marker. Singles keep their original id.
        // Stacks get a synthetic `stack-{coord}` id (stable across refetches
        // because it's derived from the coord, not from listings).
        return [...buckets.values()].map((b) => {
            const stackCount = b.entries.length;
            const minPrice = b.prices
                .map((p) => parseFloat(p))
                .filter((n) => Number.isFinite(n) && n > 0)
                .reduce((m, n) => (m == null || n < m ? n : m), null);
            // Variant: majority-rules. Mostly mirrors the cluster bubble color
            // convention so adjacent visuals stay consistent.
            const devCount = b.variants.filter((v) => v === "development").length;
            const variant = devCount * 2 >= stackCount ? "development" : "property";
            return {
                id: stackCount > 1 ? `stack-${b.key}` : b.ids[0],
                position: b.position,
                price: minPrice ?? b.prices[0],
                variant,
                stackCount,
                entries: b.entries,
                hoverIds: b.hoverIds,
            };
        });
    }, [properties, developments, state.operation]);

    if (isCollapsed) return null;

    return (
        <Box
            // CSS-hide only when `hidden` (mobile view toggle is on "list").
            // Conditional return null would unmount GoogleMap and force the
            // API loader + clusterer + every AdvancedMarker to re-init on
            // each toggle. `display: flex` is the visible default; the parent
            // controls visibility via `hidden`.
            display={hidden ? "none" : "flex"}
            flex="1 1 auto"
            h="100%"
            borderRightWidth="1px"
            borderColor="gray.200"
            position="relative"
        >
            <HideInfoWindowTail />
            <GoogleMap
                mapId={googleMapsID}
                defaultCenter={initialCenter}
                defaultZoom={initialZoom}
                gestureHandling="auto"
                disableDefaultUI={false}
                clickableIcons={false}
                // Without this, Google's MapManager rounds the initial zoom to
                // integer ~50ms after mount — fitMapToCircle/Polygon would set
                // a fractional zoom like 14.5 and Google would snap to 15
                // (or 14, depending on the rounding tie) before the first
                // paint settled. Net effect: cold-load auto-fit silently lost
                // up to one zoom level, framing the polygon either too tight
                // or too loose. With the flag on, fractional zooms stick.
                isFractionalZoomEnabled={true}
                // Strip the Google chrome we don't want, leave the
                // rotate/tilt diamond (bottom-right) alone — its
                // visibility is driven by the cloud-based map style
                // attached to our `mapId`, and Jaime prefers it as a
                // minimal control vs. the visually-heavier +/- zoom
                // pair (users pinch / scroll-wheel to zoom).
                mapTypeControl={false}
                streetViewControl={false}
                fullscreenControl={false}
                zoomControl={false}
                style={{ width: "100%", height: "100%" }}
                onDragstart={onMapDragStart}
                onIdle={(event) => {
                    if (!mapLoaded) setMapLoaded(true);
                    const map = event.map;
                    const nextBounds = map?.getBounds();
                    if (nextBounds && onBoundsChange) onBoundsChange(nextBounds);
                    // Forward camera so the parent can sync to URL + derive
                    // the API search radius. Compute radius here while the
                    // fresh bounds is in hand — passing it through state via
                    // setBounds would race the setFilters call in
                    // handleMapIdle (closure would see stale bounds).
                    const center = map?.getCenter();
                    const zoom = map?.getZoom();
                    let radiusM = null;
                    if (center && nextBounds) {
                        const ne = nextBounds.getNorthEast();
                        radiusM = haversineDistanceM(
                            center.lat(),
                            center.lng(),
                            ne.lat(),
                            ne.lng()
                        );
                    }
                    onMapIdle?.(
                        center && zoom != null
                            ? {
                                  lat: center.lat(),
                                  lng: center.lng(),
                                  zoom,
                                  radiusM,
                              }
                            : null
                    );
                }}
                onClick={() => {
                    // Clicking empty map space closes any open InfoWindow.
                    if (selectedId !== null) setSelectedId(null);
                }}
            >
                <CaptureMapRef mapRef={mapRef} />
                {mapLoaded && initialLoadDone && (
                    <>
                        {/* Markers + popups hide during draw mode so the user
                            has a clean canvas. Existing polygon overlays STAY
                            visible (P8.5 — multi-polygon UX: the user needs
                            to see what they already drew while drawing the
                            next perímetro adicional). */}
                        {!isDrawing && (
                            <>
                                <MarkersLayer
                                    key={markersEpoch}
                                    markers={markers}
                                    selectedId={selectedId}
                                    onSelect={(id) =>
                                        setSelectedId((curr) => (curr === id ? null : id))
                                    }
                                />
                                <SelectedMarkerPopup
                                    markers={markers}
                                    selectedId={selectedId}
                                    onClose={() => setSelectedId(null)}
                                    state={state}
                                />
                            </>
                        )}
                        {polygons.map((p, i) => (
                            <BoundaryOverlay key={i} polygon={p} />
                        ))}
                        {debugVisible &&
                            polygons.map((p, i) => (
                                <DebugBoundaryOverlay
                                    key={`dbg-${i}`}
                                    polygon={p}
                                    paddingPx={debugPaddingPx}
                                />
                            ))}
                        {isDrawing && (
                            <DrawingOverlay vertices={drawVertices} isPressed={isDrawPressed} />
                        )}
                    </>
                )}
            </GoogleMap>
            {/* Top-right contextual map controls. Sibling of <GoogleMap>
                so absolute positioning anchors to the outer position:relative
                Box (vis.gl uses context for Map children, so positioning
                inside <GoogleMap> wouldn't anchor here). 16px inset matches
                Google's own control insets. The slot's states:
                  - no user polygons && !isDrawing → "Dibujar perímetro"
                    (Re-centrar still appears when dragged off a page's
                    default boundary — it reads ALL polygons)
                  - user polygons >= 1 && !isDrawing → Re-centrar (if
                    dragged) + "Dibujar perímetro adicional" + Borrar
                    perímetros (multi-polygon UX hint that multi-zone
                    is supported)
                  - isDrawing → nothing (the banner takes over the top of
                    the map; no need for these controls until draw exits)
                Re-centrar (primary CTA) sits to the LEFT of the rest so
                the primary action is closer to the map content. */}
            {!isDrawing && <BrandingChip />}
            {!isDrawing && (
                <Flex
                    position="absolute"
                    top={4}
                    right={4}
                    zIndex={5}
                    gap={2}
                    pointerEvents="auto"
                    flexWrap="wrap"
                    justifyContent="flex-end"
                    maxW="calc(100% - 32px)"
                >
                    {polygons.length > 0 && cameraDragged && (
                        <RecenterButton onClick={onRecenterBoundary} />
                    )}
                    <DibujarButton onClick={onStartDraw} additional={userPolygons.length > 0} />
                    {userPolygons.length > 0 && (
                        <BoundaryButton polygons={userPolygons} onClear={onClearBoundary} />
                    )}
                </Flex>
            )}
            {isDrawing && (
                <DrawBanner
                    canApply={(drawVertices?.length ?? 0) >= 3}
                    onCancel={onCancelDraw}
                    onApply={onApplyDraw}
                />
            )}
            {/* Debug overlay — opt-in via `?debug=1` (BuscarLayout reads the
                router query). The visual padding band is a dashed inset div
                showing where CIRCLE_VIEWPORT_PADDING_PX sits relative to the
                map viewport. The Circle/Polyline reference layers are drawn
                on the map itself by DebugBoundaryOverlay. Branch-only. */}
            {/* Debug overlay — opt-in via `?debug=1` (BuscarLayout reads the
                router query). The toggle pill flips a Box of dashed inset
                showing CIRCLE_VIEWPORT_PADDING_PX (the band the filter circle
                gets fit into) plus a legend; the on-map Circles are painted
                by <DebugBoundaryOverlay> alongside the regular
                <BoundaryOverlay>. Branch-only. */}
            {debugEnabled && (
                <Box
                    as="button"
                    type="button"
                    onClick={onToggleDebug}
                    position="absolute"
                    bottom={4}
                    left={4}
                    zIndex={6}
                    fontSize="xs"
                    fontFamily="monospace"
                    px={2}
                    py={1}
                    bg={debugVisible ? "#7C3AED" : "whiteAlpha.900"}
                    color={debugVisible ? "white" : "gray.700"}
                    border="1px solid"
                    borderColor={debugVisible ? "#7C3AED" : "gray.300"}
                    borderRadius="md"
                    boxShadow="md"
                    pointerEvents="auto"
                >
                    debug · {debugVisible ? "ON" : "OFF"}
                </Box>
            )}
            {debugEnabled && debugVisible && (
                <Box
                    position="absolute"
                    bottom="44px"
                    left={4}
                    zIndex={6}
                    bg="whiteAlpha.950"
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                    boxShadow="md"
                    p={debugLegendExpanded ? 2 : 1}
                    fontSize="xs"
                    fontFamily="monospace"
                    color="gray.800"
                    maxW="300px"
                >
                    <Flex
                        as="button"
                        type="button"
                        onClick={() => setDebugLegendExpanded((v) => !v)}
                        align="center"
                        gap={1}
                        fontWeight="bold"
                        mb={debugLegendExpanded ? 1 : 0}
                        cursor="pointer"
                        _hover={{ color: "gray.600" }}
                    >
                        <Box>{debugLegendExpanded ? "▾" : "▸"}</Box>
                        <Box>Debug layers</Box>
                    </Flex>
                    {debugLegendExpanded && (
                        <>
                            <LegendRow
                                color="#9CA3AF"
                                swatch="dashed"
                                label="Google raw viewport ÷ 2 (live pick only — null on URL hydrate)"
                            />
                            <LegendRow
                                color="#002B49"
                                swatch="square"
                                label="Rendered visual (clamped + sublocality cap)"
                            />
                            <LegendRow
                                color="#F97316"
                                swatch="dashed"
                                label="Filter circle = rendered + grace → backend"
                            />
                            <LegendRow
                                color="#7C3AED"
                                swatch="square"
                                label={`Padding band: ${debugPaddingPx}px on binding axis (the only axis where it applies)`}
                            />
                            {polygons.length > 0 && (
                                <Box mt={2} pt={2} borderTop="1px solid" borderColor="gray.200">
                                    <Box fontWeight="bold" mb={1}>
                                        Polygons
                                    </Box>
                                    {polygons.map((p, i) => (
                                        <PolygonDebugInfo key={i} polygon={p} index={i + 1} />
                                    ))}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default MapPane;
